import { layersAtom, selectedLayerIdsAtom } from '@/atoms/layers.atom';
import {
  DEFAULT_PIPE_TOOL_MODE,
  pipeDetectProgressAtom,
  pipeDraftAtom,
  pipeToolModeAtom,
} from '@/atoms/pipe.atom';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { LayerType } from '@/graphql/graphql';
import { buildPipeProperties } from '@/lib/pipe/feature';
import type { Vec3 } from '@/lib/pipe/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/ui/numeric-field';
import { Progress } from '@/components/ui/progress';
import { createFeatureMutation, updateFeatureMutation } from '@/queries/feature.query';
import { createLayerMutation } from '@/queries/layer.query';
import { getProjectQuery } from '@/queries/project.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const PIPES_LAYER_NAME = 'Pipes';
/** Below this arc coverage the radius fit is poorly constrained — warn + nudge. */
const LOW_ARC_DEG = 120;

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
/** Total length of a centerline polyline (sum of segment lengths). */
const polylineLength = (vertices: Vec3[]): number => {
  let s = 0;
  for (let i = 0; i + 1 < vertices.length; i++) s += dist(vertices[i], vertices[i + 1]);
  return s;
};
/** Average of polyline vertices — a representative "center" for properties. */
const centroid = (vertices: Vec3[]): Vec3 => {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const v of vertices) {
    x += v[0];
    y += v[1];
    z += v[2];
  }
  const n = Math.max(1, vertices.length);
  return [x / n, y / n, z / n];
};

export const PipeDetectPanel = ({ projectId }: { projectId: string }) => {
  const [mode, setMode] = useAtom(pipeToolModeAtom);
  const [draft, setDraft] = useAtom(pipeDraftAtom);
  const progress = useAtomValue(pipeDetectProgressAtom);
  const layers = useAtomValue(layersAtom);
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const queryClient = useQueryClient();
  const { t } = useTranslation('project');

  const [name, setName] = useState('');

  const open = mode.enabled && (mode.phase === 'detecting' || mode.phase === 'review');
  const detecting = mode.phase === 'detecting';

  // Reset the name field whenever a fresh detection starts.
  useEffect(() => {
    if (mode.phase === 'detecting' && !mode.editFeatureId) setName('');
  }, [mode.phase, mode.editFeatureId]);

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });
  const projectSrid = projectData?.project?.srid ?? null;

  const sourceLayer = mode.sourceLayerId ? layers.find((l) => l.id === mode.sourceLayerId) : null;

  const lengthM = draft ? polylineLength(draft.vertices) : 0;
  const lowArc = !!draft && draft.arcCoverageDeg < LOW_ARC_DEG;

  const handleClose = () => {
    setMode({ ...DEFAULT_PIPE_TOOL_MODE });
    setDraft(null);
  };
  const handleCancelDetect = () => {
    // Flipping phase away from 'detecting' triggers PipeTool's effect cleanup,
    // which aborts the in-flight walk.
    setMode((m) => ({ ...m, phase: 'seeking', seed: null }));
  };
  const handleRedetect = () => {
    const seed = mode.seed ?? (draft ? centroid(draft.vertices) : null);
    if (!seed) return;
    setDraft(null);
    setMode((m) => ({ ...m, phase: 'detecting', seed }));
  };

  const ensurePipesLayer = async (): Promise<string> => {
    const existing = layers.find(
      (l) => l.type === ('VECTOR' as LayerType) && l.name === PIPES_LAYER_NAME && !l.parentLayerId,
    );
    if (existing) return existing.id;
    const created = await execute(createLayerMutation, {
      input: { projectId, name: PIPES_LAYER_NAME, type: 'VECTOR' as LayerType },
    });
    if (!created?.createLayer?.id) throw new Error(t('pipe.errorCreateLayer'));
    await queryClient.invalidateQueries({ queryKey: ['layers'] });
    return created.createLayer.id;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('no draft');
      const center = centroid(draft.vertices);
      const coords: Vec3[] = draft.vertices;
      const geometry = {
        type: 'LineString' as const,
        crs: { type: 'name' as const, properties: { name: `urn:ogc:def:crs:EPSG::${projectSrid ?? -1}` } },
        coordinates: coords,
      };

      // WGS84 reprojection for geometryWGS84 (best-effort, like stockpile).
      let geometryWGS84: typeof geometry | null = null;
      if (projectSrid !== null && projectSrid !== -1) {
        try {
          const wgs = await Promise.all(coords.map((c) => project(c, `EPSG:${projectSrid}`)));
          if (wgs.every((p) => p)) {
            geometryWGS84 = {
              type: 'LineString' as const,
              crs: { type: 'name' as const, properties: { name: `urn:ogc:def:crs:EPSG::4326` } },
              coordinates: wgs as Vec3[],
            };
          }
        } catch {
          /* proj4 missing — server falls back to geometry */
        }
      }

      const properties = buildPipeProperties({
        name: name.trim() || t('pipe.untitled'),
        radiusM: draft.radius,
        lengthM,
        axis: draft.axis,
        center,
        arcCoverageDeg: draft.arcCoverageDeg,
        rmsM: draft.rmsM,
        inlierCount: draft.inlierCount,
        confidence: draft.confidence,
        sourceLayerId: mode.sourceLayerId ?? '',
        computedAt: Date.now(),
      });

      if (mode.editFeatureId) {
        // Send geometry too — endpoint drags / extensions change the centerline,
        // and an update with only `properties` would silently discard them.
        const editInput: any = { id: mode.editFeatureId, properties, geometry };
        if (geometryWGS84) editInput.geometryWGS84 = geometryWGS84;
        const updated = await execute(updateFeatureMutation, { input: editInput });
        if (!updated?.updateFeature) throw new Error(t('pipe.errorSaveFailed'));
        if (updated.updateFeature.layerId) {
          await queryClient.invalidateQueries({ queryKey: ['features', updated.updateFeature.layerId] });
        }
        await queryClient.invalidateQueries({ queryKey: ['feature', mode.editFeatureId] });
        return updated.updateFeature;
      }

      const layerId = await ensurePipesLayer();
      const input: any = { layerId, geometry, properties };
      if (geometryWGS84) input.geometryWGS84 = geometryWGS84;
      const created = await execute(createFeatureMutation, { input });
      if (!created?.createFeature) throw new Error(t('pipe.errorSaveFailed'));
      setSelectedLayerIds((prev) => (prev.includes(layerId) ? prev : [...prev, layerId]));
      await queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      await queryClient.invalidateQueries({ queryKey: ['layers'] });
      return created.createFeature;
    },
    onSuccess: () => {
      toast.success(t('pipe.savedToast'));
      handleClose();
    },
    onError: (e: any) => toast.error(e?.message ?? t('pipe.errorSaveFailed')),
  });

  const phasePercent =
    progress && progress.total > 0 ? Math.min(100, (progress.done / progress.total) * 100) : 0;

  if (!open) return null;

  // Floating, NON-modal card (not a Sheet): no overlay, no focus trap, so the
  // user can orbit the camera and drag the endpoint handles while it's open.
  // stopPropagation keeps panel clicks from reaching the canvas pick handlers.
  return (
    <div
      className='absolute z-30 top-4 right-4 w-[320px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200/70 dark:border-stone-700/70 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md shadow-lg pointer-events-auto'
      onPointerDown={(e) => e.stopPropagation()}>
      <div className='flex items-start justify-between p-4 pb-2'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='text-foreground font-semibold'>
            {mode.editFeatureId ? t('pipe.panelTitleEdit') : t('pipe.panelTitle')}
          </h2>
          <p className='text-muted-foreground text-xs'>{t('pipe.panelDescription')}</p>
        </div>
        <button
          type='button'
          aria-label={t('pipe.discard')}
          className='text-slate-400 hover:text-slate-700 dark:hover:text-stone-100 text-lg leading-none px-1'
          onClick={handleClose}>
          ×
        </button>
      </div>

      <div className='flex flex-col gap-4 px-4 pb-4'>
          <section className='flex flex-col gap-1 text-sm'>
            <div className='flex justify-between'>
              <span className='text-slate-600 dark:text-stone-400'>{t('pipe.sourceLayer')}</span>
              <span className='font-medium truncate max-w-[200px]'>
                {sourceLayer?.name ?? t('pipe.unknownLayer')}
              </span>
            </div>
          </section>

          {/* Detecting progress */}
          {detecting && (
            <section className='flex flex-col gap-2'>
              <Progress value={phasePercent} />
              <div className='flex items-center justify-between text-xs text-slate-600 dark:text-stone-400'>
                <span>{t(`pipe.phase.${progress?.phase ?? 'seed'}` as any)}</span>
                <button type='button' className='text-red-600 hover:underline' onClick={handleCancelDetect}>
                  {t('pipe.cancel')}
                </button>
              </div>
            </section>
          )}

          {/* Results + fine-tune */}
          {mode.phase === 'review' && draft && (
            <>
              <section className='grid grid-cols-2 gap-3 rounded-md border border-slate-200 dark:border-stone-700 p-3 text-sm'>
                <Metric label={t('pipe.resultDiameter')} value={`${(draft.radius * 2).toFixed(3)} m`} prominent />
                <Metric label={t('pipe.resultLength')} value={`${lengthM.toFixed(2)} m`} prominent />
                <Metric label={t('pipe.resultRadius')} value={`${draft.radius.toFixed(3)} m`} />
                <Metric label={t('pipe.resultArcCoverage')} value={`${Math.round(draft.arcCoverageDeg)}°`} />
                <Metric label={t('pipe.resultRms')} value={`${(draft.rmsM * 1000).toFixed(1)} mm`} />
                <Metric label={t('pipe.resultInliers')} value={draft.inlierCount.toLocaleString()} />
                <Metric
                  label={t('pipe.resultConfidence')}
                  value={`${Math.round(draft.confidence * 100)}%`}
                />
              </section>

              {lowArc && (
                <div className='rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200'>
                  {t('pipe.warnLowArc', { deg: Math.round(draft.arcCoverageDeg) })}
                </div>
              )}

              <section className='flex flex-col gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <Label htmlFor='pipe-name'>{t('pipe.fieldName')}</Label>
                  <Input
                    id='pipe-name'
                    value={name}
                    placeholder={t('pipe.fieldNamePlaceholder')}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className='flex flex-col gap-1.5'>
                  <Label htmlFor='pipe-radius'>{t('pipe.fieldRadius')}</Label>
                  <NumericField
                    value={draft.radius}
                    step='0.005'
                    min='0.001'
                    onChange={(v) =>
                      setDraft((p) => (p && v > 0 ? { ...p, radius: v, edited: true } : p))
                    }
                  />
                  <p className='text-xs text-slate-600 dark:text-stone-400'>{t('pipe.fieldRadiusHelp')}</p>
                </div>

                <p className='text-xs text-slate-500 dark:text-stone-400'>{t('pipe.dragHint')}</p>
              </section>

              {/* Actions — Save full width, then a wrapping row of smaller
                  secondary actions so nothing overflows the narrow card. */}
              <div className='flex flex-col gap-2 mt-1'>
                <Button
                  size='sm'
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className='w-full'>
                  {saveMutation.isPending
                    ? t('pipe.saving')
                    : mode.editFeatureId
                      ? t('pipe.saveEdit')
                      : t('pipe.save')}
                </Button>
                <div className='flex gap-2'>
                  <Button size='sm' variant='outline' className='flex-1' onClick={handleRedetect}>
                    {t('pipe.redetect')}
                  </Button>
                  <Button size='sm' variant='ghost' className='flex-1' onClick={handleClose}>
                    {t('pipe.discard')}
                  </Button>
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

const Metric = ({ label, value, prominent }: { label: string; value: string; prominent?: boolean }) => (
  <div className='flex flex-col'>
    <span className='text-xs text-slate-500 dark:text-stone-400'>{label}</span>
    <span className={prominent ? 'text-lg font-bold text-cyan-600 dark:text-cyan-400' : 'text-sm font-medium'}>
      {value}
    </span>
  </div>
);
